// Resilient S3 multipart upload engine shared by every upload module
// (owner Uploader + guest DropUpload). It is transport-agnostic: callers pass
// an adapter with create/sign/complete/abort (and optional listParts) so the
// same retry/resume/telemetry logic works for both the Firebase-authed and the
// drop-link-authed flows.
//
// Hardening:
//   • Each part is signed+PUT as one retryable unit with exponential backoff;
//     a dropped connection pauses (waits for `online`) instead of failing.
//   • Completed part ETags are checkpointed (caller-provided session store), so
//     a reload resumes from the last finished 100MB part. We reconcile against
//     S3 via listParts when available.
//   • Emits fine-grained telemetry to a TransferMonitor (per-part state, bytes,
//     retries) powering the live packet/throughput visualization.

export const PART_SIZE = 100 * 1024 * 1024; // 100 MB
// Parallel part uploads per file. Tuned to the browser's ~6-connections-per-origin
// cap (S3's REST endpoint is HTTP/1.1, and every part hits the same host), so 6
// fills the pipe without queueing extra connections that can't actually run.
export const CONCURRENCY = 6;

export const sleep = (ms, signal) =>
  new Promise((resolve, reject) => {
    if (signal?.aborted) return reject(new DOMException('aborted', 'AbortError'));
    const t = setTimeout(done, ms);
    function done() {
      cleanup();
      resolve();
    }
    function onAbort() {
      cleanup();
      reject(new DOMException('aborted', 'AbortError'));
    }
    function cleanup() {
      clearTimeout(t);
      signal?.removeEventListener('abort', onAbort);
    }
    signal?.addEventListener('abort', onAbort);
  });

export const waitOnline = (signal) =>
  new Promise((resolve, reject) => {
    if (typeof navigator === 'undefined' || navigator.onLine !== false) return resolve();
    if (signal?.aborted) return reject(new DOMException('aborted', 'AbortError'));
    function onOnline() {
      cleanup();
      resolve();
    }
    function onAbort() {
      cleanup();
      reject(new DOMException('aborted', 'AbortError'));
    }
    function cleanup() {
      window.removeEventListener('online', onOnline);
      signal?.removeEventListener('abort', onAbort);
    }
    window.addEventListener('online', onOnline);
    signal?.addEventListener('abort', onAbort);
  });

// 4xx auth/validity errors are permanent — don't retry those.
export const isFatal = (err) => err && [400, 401, 403, 404, 410].includes(err.status);

// Retries `fn` until it succeeds or is aborted, backing off and waiting for
// connectivity. onState reports reconnect attempts.
export async function withRetry(fn, { signal, onState, onAttempt } = {}) {
  let attempt = 0;
  for (;;) {
    if (signal?.aborted) throw new DOMException('aborted', 'AbortError');
    try {
      const out = await fn();
      if (attempt > 0) onState?.({ reconnecting: false });
      return out;
    } catch (err) {
      if (signal?.aborted || err.name === 'AbortError') throw err;
      if (isFatal(err)) throw err;
      attempt += 1;
      onAttempt?.(err);
      onState?.({ reconnecting: true, attempt, error: err.message });
      // eslint-disable-next-line no-await-in-loop
      await waitOnline(signal);
      const base = Math.min(30000, 1000 * 2 ** Math.min(attempt, 5));
      // eslint-disable-next-line no-await-in-loop
      await sleep(base * (0.6 + Math.random() * 0.7), signal);
    }
  }
}

// A part attempt that makes no upload progress for this long is treated as a
// dead connection: we abort it and let withRetry sign a fresh URL and resend.
// Silent stalls (a TCP connection that quietly dies — common on flaky wifi, VPN
// drops, NAT timeouts) otherwise leave the XHR hanging forever with no error,
// which is exactly what makes an upload look "stuck" despite spare bandwidth.
export const PART_STALL_MS = 40 * 1000;

// PUT one part straight to S3 via XHR (keeps running while the tab is hidden).
export function putPart(url, blob, onPartProgress, signal, stallMs = PART_STALL_MS) {
  return new Promise((resolve, reject) => {
    const abortErr = () => new DOMException('aborted', 'AbortError');
    // Already aborted before we even start — settle immediately so the caller
    // (withRetry) sees the AbortError instead of hanging on a stuck Promise.
    if (signal?.aborted) {
      reject(abortErr());
      return;
    }
    const xhr = new XMLHttpRequest();
    let onAbort;
    let stalled = false; // true → an abort we triggered because progress stopped
    let watchdog = null;
    const armWatchdog = () => {
      if (!stallMs) return;
      clearTimeout(watchdog);
      watchdog = setTimeout(() => {
        stalled = true;
        try {
          xhr.abort();
        } catch (_e) {
          /* ignore */
        }
      }, stallMs);
    };
    const cleanup = () => {
      clearTimeout(watchdog);
      if (signal && onAbort) signal.removeEventListener('abort', onAbort);
    };
    xhr.open('PUT', url);
    xhr.upload.onprogress = (e) => {
      armWatchdog(); // progress → reset the stall timer
      onPartProgress(e.loaded);
    };
    xhr.onload = () => {
      cleanup();
      if (xhr.status >= 200 && xhr.status < 300) resolve(xhr.getResponseHeader('ETag'));
      else reject(new Error(`part upload failed (${xhr.status})`));
    };
    // Calling xhr.abort() fires the `abort` event (NOT `error`/`load`), so this
    // handler is what actually settles the Promise on cancellation — without it
    // the upload would hang and the Cancel button would appear to do nothing.
    // A watchdog-triggered abort is a transient stall (retryable); a
    // signal-triggered abort is a real user cancel (propagate AbortError).
    xhr.onabort = () => {
      cleanup();
      if (stalled && !signal?.aborted) reject(new Error('stalled — no progress'));
      else reject(abortErr());
    };
    xhr.onerror = () => {
      cleanup();
      reject(new Error('network error'));
    };
    xhr.ontimeout = () => {
      cleanup();
      reject(new Error('timed out'));
    };
    if (signal) {
      onAbort = () => xhr.abort();
      signal.addEventListener('abort', onAbort);
    }
    armWatchdog(); // also catches a connection that never sends a single byte
    xhr.send(blob);
  });
}

// Runs (or resumes) a multipart upload for one file.
//   adapter.create()                -> { uploadId, key }
//   adapter.sign(partNumber)        -> { url }
//   adapter.complete(parts)         -> result
//   adapter.abort()                 -> void
//   adapter.listParts(uploadId,key) -> { parts:[{PartNumber,ETag,Size}], gone? } (optional)
//   session = { load(), save(s), clear() }  (optional cross-reload checkpoint)
//   monitor = TransferMonitor (optional telemetry sink)
export async function runMultipart(
  file,
  { create, sign, complete, listParts, session, onProgress, onState, onRecognized, monitor, signal, fileId, partSize = PART_SIZE }
) {
  const PS = partSize;
  const partCount = Math.ceil(file.size / PS) || 1;
  const sizeOfPart = (i) => Math.min(PS, file.size - i * PS);

  // 1) Find or create the multipart upload.
  const freshUpload = async () => {
    const created = await withRetry(() => create(), { signal, onState });
    const s = {
      uploadId: created.uploadId,
      key: created.key,
      partSize: PS,
      size: file.size,
      name: file.name,
      etags: {},
      updatedAt: Date.now(),
    };
    session?.save?.(s);
    return s;
  };

  let saved = session?.load?.() || null;
  if (saved && (saved.partSize !== PS || saved.size !== file.size)) saved = null;
  // No local checkpoint? Ask the backend to create — or, for deterministic keys
  // (drop links), reattach to an in-progress multipart it still has. Either way
  // we then reconcile against S3 below, so already-uploaded parts are reused
  // even when the guest returns in a fresh browser with no local checkpoint.
  if (!saved?.uploadId) saved = await freshUpload();
  saved.etags = saved.etags || {};

  // Reconcile our checkpoint with the parts S3 actually holds. This powers
  // cross-reload resume and reclaims parts from a reattached/orphaned upload.
  // If the referenced upload is gone (completed/aborted/expired), start fresh.
  if (listParts) {
    try {
      const { parts: have, gone } = await listParts(saved.uploadId, saved.key);
      if (gone) {
        saved = await freshUpload();
      } else {
        (have || []).forEach((p) => {
          saved.etags[p.PartNumber] = p.ETag;
        });
        session?.save?.(saved);
      }
    } catch (err) {
      if (isFatal(err)) throw err; // permanent (revoked/expired) → bubble up
      // offline → keep optimistic local checkpoint
    }
  }
  const { uploadId, key } = saved;

  const doneAtStart = Object.keys(saved.etags).length;
  if (doneAtStart > 0) {
    onRecognized?.({ resumedParts: doneAtStart, totalParts: partCount, resumedBytes: doneAtStart * PS });
  }

  // 2) Prepare progress/telemetry from the resumed checkpoint.
  const loaded = new Array(partCount).fill(0);
  const parts = new Array(partCount);
  monitor?.startFile?.({ fileId: fileId || key, name: file.name, partCount, fileSize: file.size });
  for (let i = 0; i < partCount; i += 1) {
    const etag = saved.etags[i + 1];
    if (etag) {
      parts[i] = { ETag: etag, PartNumber: i + 1 };
      loaded[i] = sizeOfPart(i);
      monitor?.partState?.(i + 1, 'done', loaded[i], sizeOfPart(i));
    } else {
      monitor?.partState?.(i + 1, 'queued', 0, sizeOfPart(i));
    }
  }
  const report = () => onProgress?.(Math.min(loaded.reduce((a, b) => a + b, 0) / (file.size || 1), 1));
  report();

  // 3) Upload remaining parts with a worker pool.
  let next = 0;
  async function worker() {
    for (;;) {
      const i = next;
      next += 1;
      if (i >= partCount) return;
      if (saved.etags[i + 1]) continue;
      const start = i * PS;
      const blob = file.slice(start, Math.min(start + PS, file.size));
      monitor?.partState?.(i + 1, 'active', 0, blob.size);
      let lastLoaded = 0;
      let lastWire = 0; // bytes counted toward wire throughput for this attempt
      // eslint-disable-next-line no-await-in-loop
      const etag = await withRetry(
        async () => {
          const { url } = await sign(uploadId, key, i + 1);
          lastWire = 0; // a fresh attempt's XHR progress restarts at 0
          return putPart(
            url,
            blob,
            (b) => {
              monitor?.addWire?.(b - lastWire); // real bytes since last progress tick
              lastWire = b;
              loaded[i] = b;
              lastLoaded = b;
              monitor?.partState?.(i + 1, 'active', b, blob.size);
              report();
            },
            signal
          );
        },
        {
          signal,
          onState,
          onAttempt: () => {
            monitor?.addRetry?.(lastLoaded); // bytes we'll have to resend
            monitor?.partState?.(i + 1, 'retry', loaded[i], blob.size);
            lastLoaded = 0;
          },
        }
      );
      saved.etags[i + 1] = etag;
      saved.updatedAt = Date.now();
      session?.save?.(saved);
      parts[i] = { ETag: etag, PartNumber: i + 1 };
      loaded[i] = blob.size;
      monitor?.partState?.(i + 1, 'done', blob.size, blob.size);
      report();
    }
  }
  await Promise.all(Array.from({ length: Math.min(CONCURRENCY, partCount) }, worker));

  // 4) Finalize.
  const ordered = parts.filter(Boolean).sort((a, b) => a.PartNumber - b.PartNumber);
  const result = await withRetry(() => complete(uploadId, key, ordered), { signal, onState });
  session?.clear?.();
  monitor?.finishFile?.();
  onProgress?.(1);
  return { key, result };
}
